import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ENDPOINT } from 'constants/variables';

export default function MicrosoftMailConnection({ userId, email }) {
  const token = useSelector(state => state.loginUser.token);
  const owner = useSelector(state => state.loginUser.useraccount_id);
  const { i18n } = useTranslation();
  const es = !i18n.language.startsWith('en');
  const [link, setLink] = useState(null);
  const [connected, setConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const own = Boolean(userId) && String(userId) === String(owner);
  const request = useCallback(async (path, method = 'GET', body, signal) => {
    const response = await fetch(`${ENDPOINT}/mail/microsoft/${path}`, {
      method, signal, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    if (!response.ok) {
      throw new Error(response.status === 503
        ? (es ? 'La conexión con Microsoft aún no está configurada en el servidor.' : 'Microsoft connection is not configured on the server yet.')
        : (es ? 'No se pudo conectar. Guarda el correo e inicia de nuevo con esa misma cuenta de Microsoft.' : 'Unable to connect. Save the email and try again using that same Microsoft account.'));
    }
    return response.status === 204 || method === 'DELETE' ? null : response.json();
  }, [token, es]);
  useEffect(() => {
    setLink(null); setConnected(false); setError('');
    if (!own) return;
    const abort = new AbortController();
    request('status', 'GET', undefined, abort.signal)
      .then(result => setConnected(result.connected))
      .catch(err => { if (err.name !== 'AbortError') setError(err.message); });
    return () => abort.abort();
  }, [own, userId, email, request]);
  useEffect(() => {
    if (!link) return;
    let timer;
    let canceled = false;
    const abort = new AbortController();
    const poll = async () => {
      if (Date.now() >= link.expiresAt) {
        setLink(null); setError(es ? 'El código ha caducado. Vuelve a conectar.' : 'Code expired. Connect again.'); return;
      }
      try {
        const result = await request('poll', 'POST', undefined, abort.signal);
        if (canceled) return;
        if (result.status === 'connected') { setConnected(true); setLink(null); return; }
        timer = setTimeout(poll, Math.max(5, result.interval) * 1000);
      } catch (err) {
        if (!canceled) { setError(err.message); setLink(null); }
      }
    };
    timer = setTimeout(poll, link.interval * 1000);
    return () => { canceled = true; clearTimeout(timer); abort.abort(); };
  }, [link, request, es]);
  const connect = async () => {
    setBusy(true); setError('');
    try {
      const result = await request('connect', 'POST', { email });
      setLink({ ...result, expiresAt: Date.now() + result.expiresIn * 1000 });
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true); setError('');
    try { await request('connection', 'DELETE'); setConnected(false); setLink(null); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  if (!own) return <Alert severity="info">{es
    ? 'Guarda el usuario y entra en Gestresca con su sesión para conectar su correo con Microsoft.'
    : 'Save this user and sign in to Gestresca as that user to connect Microsoft email.'}</Alert>;
  return <Box sx={{ my: 2 }}>
    <Typography>{es ? 'Autoriza el envío desde Microsoft. No necesitas introducir la contraseña del correo.' : 'Authorize sending through Microsoft. No email password is needed.'}</Typography>
    {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
    {connected && <Alert severity="success" sx={{ my: 1 }}>{es ? 'Correo conectado con Microsoft' : 'Email connected to Microsoft'}</Alert>}
    {link && <Alert severity="info" sx={{ my: 1 }}>
      <Typography>{es ? 'Abre Microsoft e introduce este código con la cuenta' : 'Open Microsoft and enter this code using the account'} {email}:</Typography>
      <Typography component="strong" sx={{ display: 'block', fontSize: 24, letterSpacing: 2 }}>{link.userCode}</Typography>
      <Button component="a" href="https://microsoft.com/devicelogin" target="_blank" rel="noopener noreferrer">{es ? 'Abrir Microsoft' : 'Open Microsoft'}</Button>
      <Typography>{es ? 'Esperando tu autorización…' : 'Waiting for your authorization…'}</Typography>
    </Alert>}
    <Button variant="outlined" disabled={busy || Boolean(link)} onClick={connect}>{es ? 'Conectar con Microsoft' : 'Connect to Microsoft'}</Button>
    {(connected || link) && <Button disabled={busy} onClick={disconnect}>{es ? 'Desconectar' : 'Disconnect'}</Button>}
  </Box>;
}
